import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Form,
  Input,
  Button,
  Select,
  Modal,
  message,
  DatePicker,
  InputNumber,
  Space,
  Divider,
  Popconfirm,
  Table,
  Checkbox,
  Radio,
  Slider,
  Rate,
  Image,

  Tooltip,
  Tag,
  ConfigProvider
} from 'antd';
import { 
  CoffeeEquipmentCategory, 
  CoffeeEquipmentField, 
  CoffeeEquipment 
} from '../types/coffeeEquipment';
import { CoffeeEquipmentFieldsManager } from '../components/CoffeeEquipmentFieldsManager';
import { CoffeeEquipmentService } from '../services/coffeeEquipmentService';
import { formatInputNumberFormatter, parseFlexibleNumber } from '../utils/numberInput';
import {
  PlusOutlined,
  DeleteOutlined,
  AppstoreOutlined,
  PlusCircleOutlined,
  EditOutlined,
  QuestionCircleOutlined
} from '@ant-design/icons';
import viVN from 'antd/locale/vi_VN';
import dayjs from 'dayjs';
import 'dayjs/locale/vi';
import { RcFile } from 'antd/es/upload';
import AdminLayout from '../components/AdminLayout';
import '../styles/AdminPages.css';
import { ColumnType } from 'antd/es/table';
import useDrivePicker from 'react-google-drive-picker';
import type { TourProps } from 'antd';
import { Tour } from 'antd';

dayjs.locale('vi');
const { TextArea } = Input;
const { Search } = Input;

// Generate random code function
const generateRandomCode = (prefix: string, length: number) => {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * characters.length));
  }
  return `${prefix}-${result}`;
};

const coffeeEquipmentService = new CoffeeEquipmentService();

export const CoffeeEquipmentManagement: React.FC = () => {
  type CategoryEquipmentCacheEntry = {
    equipment: CoffeeEquipment[];
    fetchedAt: number;
  };

  const [categories, setCategories] = useState<CoffeeEquipmentCategory[]>([]);
  const [isNewCategoryModalVisible, setIsNewCategoryModalVisible] = useState(false);
  const [isEquipmentModalVisible, setIsEquipmentModalVisible] = useState(false);
  const [isEquipmentListModalVisible, setIsEquipmentListModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [categoryForm] = Form.useForm();
  const [equipmentForm] = Form.useForm();
  const [currentCategory, setCurrentCategory] = useState<CoffeeEquipmentCategory | null>(null);
  const [editedFields, setEditedFields] = useState<CoffeeEquipmentField[]>([]);
  const [isEditFieldsModalVisible, setIsEditFieldsModalVisible] = useState(false);
  const [editingCategory, setEditingCategory] = useState<CoffeeEquipmentCategory | null>(null);
  const [equipment, setEquipment] = useState<CoffeeEquipment[]>([]);
  const [categoryEquipment, setCategoryEquipment] = useState<{ [key: string]: number }>({});
  const [isEditing, setIsEditing] = useState(false);
  const [editingEquipment, setEditingEquipment] = useState<CoffeeEquipment | null>(null);
  const [newOptionText, setNewOptionText] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<RcFile[]>([]);
  const [selectedFilesPreview, setSelectedFilesPreview] = useState<any[]>([]);
  const [driveImages, setDriveImages] = useState<{ fileId: string; webViewLink: string; fileName: string; }[]>([]);
  const [driveImagesPreview, setDriveImagesPreview] = useState<{ fileId: string; webViewLink: string; fileName: string; }[]>([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [open, setOpen] = useState<boolean>(false);

  // Refs for tour
  const titleRef = useRef<HTMLDivElement>(null);
  const addButtonRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<any>(null);
  const tableRef = useRef<HTMLDivElement>(null);

  const [openPicker] = useDrivePicker();

  const equipmentByCategoryCacheRef = useRef<Map<string, CategoryEquipmentCacheEntry>>(new Map());
  const equipmentByCategoryInFlightRef = useRef<Map<string, Promise<CoffeeEquipment[]>>>(new Map());
  const uploadProgressIntervalRef = useRef<number | null>(null);
  const localPreviewObjectUrlsRef = useRef<string[]>([]);
  const localPreviewUrlByFileRef = useRef<Map<RcFile, string>>(new Map());

  const setCategoryEquipmentCount = useCallback((categoryId: string, count: number) => {
    setCategoryEquipment(prev => {
      if (prev[categoryId] === count) return prev;
      return { ...prev, [categoryId]: count };
    });
  }, []);

  const bumpCategoryEquipmentCount = useCallback((categoryId: string, delta: number) => {
    setCategoryEquipment(prev => {
      const current = prev[categoryId] ?? 0;
      const next = Math.max(0, current + delta);
      if (current === next) return prev;
      return { ...prev, [categoryId]: next };
    });
  }, []);

  const upsertEquipmentByCategoryCache = useCallback((categoryId: string, list: CoffeeEquipment[]) => {
    equipmentByCategoryCacheRef.current.set(categoryId, { equipment: list, fetchedAt: Date.now() });
  }, []);

  const getEquipmentByCategoryCached = useCallback(async (categoryId: string) => {
    const cached = equipmentByCategoryCacheRef.current.get(categoryId);
    if (cached) return cached.equipment;

    const inFlight = equipmentByCategoryInFlightRef.current.get(categoryId);
    if (inFlight) return inFlight;

    const request = coffeeEquipmentService
      .getEquipmentByCategory(categoryId)
      .then((fetched) => {
        upsertEquipmentByCategoryCache(categoryId, fetched);
        return fetched;
      })
      .finally(() => {
        equipmentByCategoryInFlightRef.current.delete(categoryId);
      });

    equipmentByCategoryInFlightRef.current.set(categoryId, request);
    return request;
  }, [upsertEquipmentByCategoryCache]);

  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadMissingEquipmentCounts = async () => {
      if (categories.length === 0) return;

      const missingCategoryIds: string[] = [];
      const countsFromCache: { [key: string]: number } = {};

      for (const category of categories) {
        const cached = equipmentByCategoryCacheRef.current.get(category.id);
        if (cached) {
          countsFromCache[category.id] = cached.equipment.length;
        } else {
          missingCategoryIds.push(category.id);
        }
      }

      if (Object.keys(countsFromCache).length > 0) {
        setCategoryEquipment(prev => ({ ...prev, ...countsFromCache }));
      }

      if (missingCategoryIds.length === 0) return;

      const results = await Promise.all(missingCategoryIds.map(async (categoryId) => {
        const list = await getEquipmentByCategoryCached(categoryId);
        return { categoryId, list };
      }));

      if (cancelled) return;

      setCategoryEquipment(prev => {
        const next = { ...prev };
        for (const { categoryId, list } of results) {
          next[categoryId] = list.length;
        }
        return next;
      });
    };

    void loadMissingEquipmentCounts();
    return () => {
      cancelled = true;
    };
  }, [categories, getEquipmentByCategoryCached]);

  const filteredEquipment = useMemo(() => {
    if (!equipment.length) return equipment;
    const searchValue = searchText.trim().toLowerCase();
    if (!searchValue) return equipment;

    return equipment.filter(item => {
      if (item.categoryName?.toLowerCase().includes(searchValue)) return true;
      return item.values.some(field => {
        if (typeof field.value === 'string') {
          return field.value.toLowerCase().includes(searchValue);
        }
        if (typeof field.value === 'number') {
          return field.value.toString().includes(searchValue);
        }
        return false;
      });
    });
  }, [equipment, searchText]);

  useEffect(() => {
    const previewObjectUrlsRef = localPreviewObjectUrlsRef;
    const previewUrlByFileRef = localPreviewUrlByFileRef;

    return () => {
      if (uploadProgressIntervalRef.current !== null) {
        window.clearInterval(uploadProgressIntervalRef.current);
        uploadProgressIntervalRef.current = null;
      }
      if (previewObjectUrlsRef.current.length > 0) {
        previewObjectUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
        previewObjectUrlsRef.current = [];
      }
      if (previewUrlByFileRef.current.size > 0) {
        Array.from(previewUrlByFileRef.current.values()).forEach((url) => {
          URL.revokeObjectURL(url);
        });
        previewUrlByFileRef.current.clear();
      }
    };
  }, []);

  const loadCategories = async () => {
    try {
      setLoading(true);
      const fetchedCategories = await coffeeEquipmentService.getAllCategories();
      setCategories(fetchedCategories);
    } catch (error) {
      message.error('Không thể tải danh sách loại dụng cụ');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCategory = async () => {
    try {
      const values = await categoryForm.validateFields();
      setLoading(true);

      const newCategory = await coffeeEquipmentService.createCategory({
        name: values.name,
        description: values.description || '',
        fields: values.fields || [],
      });

      setCategories(prev => [...prev, newCategory]);
      setCurrentCategory(newCategory);
      setEditedFields(newCategory.fields);
      setIsNewCategoryModalVisible(false);
      setCategoryEquipmentCount(newCategory.id, 0);
      message.success('Tạo loại dụng cụ thành công');
    } catch (error) {
      console.error('Validation failed:', error);
      message.error('Không thể tạo loại dụng cụ');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (equipment: CoffeeEquipment) => {
    setEditingEquipment(equipment);
    setIsEditing(true);
    setIsEquipmentListModalVisible(false);
    setIsEquipmentModalVisible(true);

    equipmentForm.resetFields();

    // Reset image states
    setSelectedFiles([]);
    setDriveImages([]);
    setUploadProgress(0);

    // Set existing images for preview
    if (equipment.driveImages) {
      setDriveImagesPreview(equipment.driveImages.map(img => ({
        ...img,
        fileName: img.fileName || 'Untitled'
      })));
    } else {
      setDriveImagesPreview([]);
    }

    if (equipment.images) {
      setSelectedFilesPreview(equipment.images);
    } else {
      setSelectedFilesPreview([]);
    }

    const formValues = equipment.values.reduce((acc: { [key: string]: any }, field) => {
      const value = field.value;
      const fieldConfig = currentCategory?.fields.find(f => f.id === field.id);

      if (fieldConfig) {
        fieldConfig.tooltip = field.tooltip;
      }

      if (fieldConfig?.type === 'date' && value) {
        acc[field.id] = dayjs(value.toDate ? value.toDate() : value);
      } else if (fieldConfig?.type === 'checkbox') {
        acc[field.id] = Boolean(value);
      } else if (fieldConfig?.type === 'multi_checkbox' && typeof value === 'object') {
        const selectedValues = Object.entries(value)
          .filter(([_, checked]) => checked)
          .map(([id]) => id);
        acc[field.id] = selectedValues;
      } else if (typeof value === 'object' && value?.from !== undefined && value?.to !== undefined) {
        acc[field.id] = {
          from: value.from,
          to: value.to
        };
      } else {
        acc[field.id] = value;
      }
      return acc;
    }, {});



    equipmentForm.setFieldsValue(formValues);
  };

  const handleImagesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setSelectedFiles(prev => [...prev, ...files.map(f => f as RcFile)]);
  };

  const handleOpenPicker = () => {
    openPicker({
      clientId: "85231195954-hsetf3cpqpfcr67p2k40001og3054t52.apps.googleusercontent.com",
      developerKey: "AIzaSyD-xnVTuCx7ZDPzxrTmeL4my9FuIfkpYtY",
      viewId: "DOCS",
      showUploadView: true,
      showUploadFolders: true,
      supportDrives: true,
      multiselect: false,
      callbackFunction: (data) => {
        if (data.action === 'picked') {
          const file = data.docs[0];
          setDriveImages(prev => [...prev, {
            fileId: file.id,
            webViewLink: file.url || '',
            fileName: file.name
          }]);
        }
      },
    });
  };

  const handleRemoveLocalImage = (index: number) => {
    setSelectedFiles(prev => {
      const removed = prev[index];
      if (removed) {
        const url = localPreviewUrlByFileRef.current.get(removed);
        if (url) {
          URL.revokeObjectURL(url);
          localPreviewUrlByFileRef.current.delete(removed);
        }
      }
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleRemoveLocalPreviewImage = (index: number) => {
    setSelectedFilesPreview(prev => prev.filter((_, i) => i !== index));
  };

  const handleRemoveDriveImage = (index: number) => {
    setDriveImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleRemoveDrivePreviewImage = (index: number) => {
    setDriveImagesPreview(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    try {
      const values = await equipmentForm.validateFields();

      if (!currentCategory) {
        message.error('Vui lòng chọn loại dụng cụ');
        return;
      }

      setSubmitLoading(true);
      setUploadProgress(0);

      const lstDriveImages: any[] = [];
      const newsLstImage: any[] = [];
      const oldLstImage: any[] = [];

      selectedFiles.forEach(file => {
        newsLstImage.push(file);
      });

      selectedFilesPreview?.forEach(file => {
        oldLstImage.push(file);
      });

      driveImages.forEach(image => {
        lstDriveImages.push({
          fileId: image.fileId,
          webViewLink: image.webViewLink,
          fileName: image.fileName
        });
      });

      driveImagesPreview?.forEach(image => {
        lstDriveImages.push({
          fileId: image.fileId,
          webViewLink: image.webViewLink,
          fileName: image.fileName
        });
      });

      // Show progress indicator if there are images to upload
      if (newsLstImage.length > 0) {
        setUploadProgress(10);
        if (uploadProgressIntervalRef.current !== null) {
          window.clearInterval(uploadProgressIntervalRef.current);
          uploadProgressIntervalRef.current = null;
        }
        uploadProgressIntervalRef.current = window.setInterval(() => {
          setUploadProgress(prev => {
            if (prev >= 90) {
              if (uploadProgressIntervalRef.current !== null) {
                window.clearInterval(uploadProgressIntervalRef.current);
                uploadProgressIntervalRef.current = null;
              }
              return 90;
            }
            return prev + 5;
          });
        }, 1000);
      }

      // Process auto_text fields
      const autoTextFields = currentCategory.fields.filter(field => field.type === 'auto_text');
      if (autoTextFields.length > 0) {
        const needsAutoText = autoTextFields.some(field => !values[field.id] && field.autoConfig);
        if (needsAutoText) {
          const existingEquipment = await getEquipmentByCategoryCached(currentCategory.id);

          for (const field of autoTextFields) {
            if (!values[field.id] && field.autoConfig) {
              let isUnique = false;
              let randomCode = '';

              while (!isUnique) {
                const currentCode = generateRandomCode(field.autoConfig.prefix, field.autoConfig.length);

                const codeExists = existingEquipment.some(equipment =>
                  equipment.values.some(v => v.id === field.id && v.value === currentCode)
                );

                if (!codeExists) {
                  randomCode = currentCode;
                  isUnique = true;
                }
              }

              values[field.id] = randomCode;
            }
          }
        }
      }

      // Process field values
      const processedValues: Array<{
        id: string;
        name: string;
        value: any;
        type: 'text' | 'number' | 'number_range' | 'textarea' | 'date' | 'select' | 'checkbox' | 'radio' | 'range' | 'rating' | 'image' | 'multi_input' | 'auto_text' | 'multi_checkbox';
        tooltip?: string;
      }> = [];

      currentCategory.fields.forEach(field => {
        const value = values[field.id];
        let processedValue;

        if (field.type === 'date' && value) {
          processedValue = value.toDate();
        } else if (field.type === 'number_range' && value) {
          processedValue = {
            from: value.from || 0,
            to: value.to || 0
          };
        } else if (field.type === 'image') {
          processedValue = [];
        } else if (field.type === 'multi_input') {
          if (Array.isArray(value)) {
            processedValue = value.map((item) => {
              const result: { [key: string]: any } = {};
              if (field.multiInputConfig) {
                field.multiInputConfig.valueKeys.forEach((key, keyIndex) => {
                  result[key] = item[field.multiInputConfig?.valueNames[keyIndex] || ''] || 0;
                });
              }
              return result;
            });
          } else {
            processedValue = [];
          }
        } else if (field.type === 'select') {
          processedValue = value || [];
        } else if (field.type === 'number') {
          processedValue = Number(value) || 0;
        } else if (field.type === 'auto_text') {
          processedValue = value || generateRandomCode(field.autoConfig?.prefix || 'ITEM', field.autoConfig?.length || 4);
        } else if (field.type === 'checkbox') {
          processedValue = Boolean(value);
        } else if (field.type === 'multi_checkbox') {
          const checkboxValues: { [key: string]: boolean } = {};
          if (field.checkboxConfig?.items) {
            field.checkboxConfig.items.forEach(item => {
              checkboxValues[item.id] = (value || []).includes(item.id);
            });
          }
          processedValue = checkboxValues;
        } else {
          processedValue = value || '';
        }

        processedValues.push({
          id: field.id,
          name: field.name,
          value: processedValue,
          type: field.type,
          ...(field.tooltip ? { tooltip: field.tooltip } : {})
        });
      });

      // Prepare equipment data
      const equipmentData = {
        categoryId: currentCategory.id,
        values: processedValues,
        driveImages: lstDriveImages
      };

      if (isEditing && editingEquipment) {
        await coffeeEquipmentService.updateEquipment(
          editingEquipment.id,
          equipmentData,
          newsLstImage,
          oldLstImage
        );

        setEquipment(prev =>
          prev.map(e =>
            e.id === editingEquipment.id
              ? { ...e, ...equipmentData }
              : e
          )
        );

        const cache = equipmentByCategoryCacheRef.current.get(currentCategory.id);
        if (cache) {
          equipmentByCategoryCacheRef.current.set(currentCategory.id, {
            ...cache,
            equipment: cache.equipment.map(e =>
              e.id === editingEquipment.id
                ? { ...e, ...equipmentData }
                : e
            ),
            fetchedAt: Date.now()
          });
        }

        message.success('Cập nhật dụng cụ thành công');
      } else {
        const newEquipment = await coffeeEquipmentService.createEquipment(equipmentData, selectedFiles);

        if (isEquipmentListModalVisible) {
          setEquipment(prev => [...prev, newEquipment]);
        }

        const cache = equipmentByCategoryCacheRef.current.get(currentCategory.id);
        if (cache) {
          equipmentByCategoryCacheRef.current.set(currentCategory.id, {
            ...cache,
            equipment: [...cache.equipment, newEquipment],
            fetchedAt: Date.now()
          });
        }
        bumpCategoryEquipmentCount(currentCategory.id, 1);

        message.success('Thêm dụng cụ thành công');
      }

      setUploadProgress(100);
      setIsEquipmentModalVisible(false);
      setIsEditing(false);
      setEditingEquipment(null);
      setSelectedFiles([]);
      setSelectedFilesPreview([]);
      setDriveImages([]);
      setDriveImagesPreview([]);
      if (localPreviewUrlByFileRef.current.size > 0) {
        Array.from(localPreviewUrlByFileRef.current.values()).forEach((url) => {
          URL.revokeObjectURL(url);
        });
        localPreviewUrlByFileRef.current.clear();
      }
      equipmentForm.resetFields();
    } catch (error) {
      console.error('Validation failed:', error);
      message.error(isEditing ? 'Không thể cập nhật dụng cụ' : 'Không thể thêm dụng cụ');
    } finally {
      setSubmitLoading(false);
      if (uploadProgressIntervalRef.current !== null) {
        window.clearInterval(uploadProgressIntervalRef.current);
        uploadProgressIntervalRef.current = null;
      }
      setTimeout(() => setUploadProgress(0), 1000);
    }
  };

  const handleDeleteCategory = async (categoryId: string) => {
    try {
      setLoading(true);

      // Hiển thị thông báo bắt đầu xóa
      const hideLoading = message.loading('Đang xóa loại dụng cụ và tất cả dữ liệu liên quan...', 0);

      // Delete all equipment in this category (each deleteEquipment call will clean its related data)
      const categoryEquipmentList = await getEquipmentByCategoryCached(categoryId);
      for (const equipment of categoryEquipmentList) {
        await coffeeEquipmentService.deleteEquipment(equipment.id);
      }

      // Delete category
      await coffeeEquipmentService.deleteCategory(categoryId);

      // Ẩn thông báo loading
      hideLoading();

      // Update state
      setCategories(prev => prev.filter(cat => cat.id !== categoryId));
      if (currentCategory && currentCategory.id === categoryId) {
        setCurrentCategory(null);
        setEditedFields([]);
      }
      setCategoryEquipment(prev => {
        if (!(categoryId in prev)) return prev;
        const next = { ...prev };
        delete next[categoryId];
        return next;
      });
      equipmentByCategoryCacheRef.current.delete(categoryId);
      equipmentByCategoryInFlightRef.current.delete(categoryId);

      message.success({
        content: 'Đã xóa loại dụng cụ và tất cả dữ liệu liên quan thành công!',
        duration: 3,
      });
    } catch (error) {
      console.error('Error deleting category:', error);
      message.error('Không thể xóa loại dụng cụ');
    } finally {
      setLoading(false);
    }
  };

  const handleEditFields = async () => {
    try {
      if (!editingCategory) return;

      setLoading(true);

      // Clean and process fields before updating
      const cleanFields = editedFields.map(field => {
        const cleanField = { ...field } as CoffeeEquipmentField;
        const validKeys = [
          'id',
          'name',
          'type',
          'required',
          'placeholder',
          'options',
          'min',
          'max',
          'step',
          'multiple',
          'accept',
          'defaultValue',
          'allowDriveUpload',
          'maxFiles',
          'multiInputConfig',
          'checkboxConfig',
          'tooltip',
          'suffix',
          'autoConfig'
        ] as const;

        const filteredField = validKeys.reduce((acc, key) => {
          if (cleanField[key] !== undefined) {
            acc[key] = cleanField[key];
          }
          return acc;
        }, {} as Partial<CoffeeEquipmentField>);

        if (cleanField.type === 'multi_input' && cleanField.multiInputConfig) {
          filteredField.multiInputConfig = {
            valueKeys: [...cleanField.multiInputConfig.valueKeys],
            valueNames: [...cleanField.multiInputConfig.valueNames],
          };
        }

        if (cleanField.type === 'multi_checkbox' && cleanField.checkboxConfig) {
          filteredField.checkboxConfig = {
            items: cleanField.checkboxConfig.items.map(item => ({
              id: item.id,
              label: item.label
            }))
          };
        }

        if (cleanField.type === 'auto_text' && cleanField.autoConfig) {
          filteredField.autoConfig = {
            prefix: cleanField.autoConfig.prefix,
            length: cleanField.autoConfig.length
          };
        }

        return filteredField as CoffeeEquipmentField;
      });

      // Get deleted field IDs
      const originalFieldIds = editingCategory.fields.map(f => f.id);
      const newFieldIds = cleanFields.map(f => f.id);
      const deletedFieldIds = originalFieldIds.filter(id => !newFieldIds.includes(id));

      // Update category with new fields
      await coffeeEquipmentService.updateCategory(editingCategory.id, {
        fields: cleanFields
      });

      // If fields were deleted, update all equipment
      if (deletedFieldIds.length > 0) {
        const categoryEquipmentList = await getEquipmentByCategoryCached(editingCategory.id);

        for (const equipment of categoryEquipmentList) {
          const updatedValues = equipment.values.filter(v => !deletedFieldIds.includes(v.id));
          await coffeeEquipmentService.updateEquipment(equipment.id, {
            values: updatedValues
          });
        }

        const cache = equipmentByCategoryCacheRef.current.get(editingCategory.id);
        if (cache) {
          equipmentByCategoryCacheRef.current.set(editingCategory.id, {
            ...cache,
            equipment: cache.equipment.map(e => ({
              ...e,
              values: e.values.filter(v => !deletedFieldIds.includes(v.id)),
            })),
            fetchedAt: Date.now(),
          });
        }
      }

      // Update state
      setCategories(prev => prev.map(cat =>
        cat.id === editingCategory.id
          ? { ...cat, fields: cleanFields }
          : cat
      ));
      setCurrentCategory(prev => {
        if (!prev) return prev;
        if (prev.id !== editingCategory.id) return prev;
        return { ...prev, fields: cleanFields };
      });
      setEditingCategory(prev => {
        if (!prev) return prev;
        if (prev.id !== editingCategory.id) return prev;
        return { ...prev, fields: cleanFields };
      });

      setIsEditFieldsModalVisible(false);
      message.success('Cập nhật trường thông tin thành công');
    } catch (error) {
      console.error('Error updating fields:', error);
      message.error('Không thể cập nhật trường thông tin');
    } finally {
      setLoading(false);
    }
  };

  const getLocalPreviewUrl = useCallback((file: RcFile) => {
    const existing = localPreviewUrlByFileRef.current.get(file);
    if (existing) return existing;
    const url = URL.createObjectURL(file);
    localPreviewUrlByFileRef.current.set(file, url);
    localPreviewObjectUrlsRef.current.push(url);
    return url;
  }, []);

  const renderField = (field: CoffeeEquipmentField) => {
    const rules = [{ required: field.required, message: `${field.name} là bắt buộc` }];

    switch (field.type) {
      case 'number_range':
        return (
          <Form.Item key={field.id} name={field.id} label={
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>{field.name}</span>
              {field.tooltip && (
                <Tooltip title={field.tooltip}>
                  <span style={{ color: '#1890ff', cursor: 'help' }}>(?)</span>
                </Tooltip>
              )}
            </div>
          } rules={rules}>
            <Input.Group compact>
              <Form.Item
                name={[field.id, 'from']}
                noStyle
                rules={[{ required: field.required, message: 'Vui lòng nhập giá trị từ' }]}
              >
                <InputNumber
                  placeholder="Từ"
                  style={{ width: '120px' }}
                  min={field.min}
                  max={field.max}
                  step={field.step}
                  formatter={formatInputNumberFormatter(6)}
                  parser={(value) => parseFlexibleNumber(value) as any}
                  addonAfter={field.suffix}
                />
              </Form.Item>
              <span style={{ padding: '0 8px' }}>-</span>
              <Form.Item
                name={[field.id, 'to']}
                noStyle
                rules={[{ required: field.required, message: 'Vui lòng nhập giá trị đến' }]}
              >
                <InputNumber
                  placeholder="Đến"
                  style={{ width: '120px' }}
                  min={field.min}
                  max={field.max}
                  step={field.step}
                  formatter={formatInputNumberFormatter(6)}
                  parser={(value) => parseFlexibleNumber(value) as any}
                  addonAfter={field.suffix}
                />
              </Form.Item>
            </Input.Group>
          </Form.Item>
        );
      case 'textarea':
        return (
          <Form.Item key={field.id} name={field.id} label={
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>{field.name}</span>
              {field.tooltip && (
                <Tooltip title={field.tooltip}>
                  <span style={{ color: '#1890ff', cursor: 'help' }}>(?)</span>
                </Tooltip>
              )}
            </div>
          } rules={rules}>
            <TextArea placeholder={field.placeholder} rows={4} />
          </Form.Item>
        );
      case 'number':
        return (
          <Form.Item key={field.id} name={field.id} label={
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>{field.name}</span>
              {field.tooltip && (
                <Tooltip title={field.tooltip}>
                  <span style={{ color: '#1890ff', cursor: 'help' }}>(?)</span>
                </Tooltip>
              )}
            </div>
          } rules={rules}>
            <InputNumber
              placeholder={field.placeholder}
              min={field.min}
              max={field.max}
              step={field.step}
              style={{ width: '100%' }}
              formatter={formatInputNumberFormatter(6)}
              parser={(value) => parseFlexibleNumber(value) as any}
              addonAfter={field.suffix}
            />
          </Form.Item>
        );
      case 'date':
        return (
          <Form.Item key={field.id} name={field.id} label={
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>{field.name}</span>
              {field.tooltip && (
                <Tooltip title={field.tooltip}>
                  <span style={{ color: '#1890ff', cursor: 'help' }}>(?)</span>
                </Tooltip>
              )}
            </div>
          } rules={rules}>
            <DatePicker
              placeholder="Chọn ngày"
              style={{ width: '100%' }}
              format="DD/MM/YYYY"
            />
          </Form.Item>
        );
      case 'select':
        return (
          <Form.Item key={field.id} name={field.id} label={
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>{field.name}</span>
              {field.tooltip && (
                <Tooltip title={field.tooltip}>
                  <span style={{ color: '#1890ff', cursor: 'help' }}>(?)</span>
                </Tooltip>
              )}
            </div>
          } rules={rules}>
            <Select
              mode="multiple"
              placeholder={field.placeholder || "Chọn một hoặc nhiều giá trị"}
              options={field.options}
              style={{ width: '100%' }}
              allowClear
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              dropdownRender={(menu) => (
                <>
                  {menu}
                  <Divider style={{ margin: '8px 0' }} />
                  <Space style={{ padding: '0 8px 4px' }}>
                    <Input
                      placeholder="Nhập lựa chọn mới"
                      value={newOptionText}
                      onChange={(e) => setNewOptionText(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleAddNewOption(field.id);
                        }
                      }}
                    />
                    <Button type="text" icon={<PlusOutlined />} onClick={() => handleAddNewOption(field.id)}>
                      Thêm
                    </Button>
                  </Space>
                </>
              )}
            />
          </Form.Item>
        );
      case 'checkbox':
        return (
          <Form.Item key={field.id} name={field.id} label={
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>{field.name}</span>
              {field.tooltip && (
                <Tooltip title={field.tooltip}>
                  <span style={{ color: '#1890ff', cursor: 'help' }}>(?)</span>
                </Tooltip>
              )}
            </div>
          } rules={rules} valuePropName="checked">
            <Checkbox />
          </Form.Item>
        );
      case 'multi_checkbox':
        return (
          <Form.Item key={field.id} name={field.id} label={
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>{field.name}</span>
              {field.tooltip && (
                <Tooltip title={field.tooltip}>
                  <span style={{ color: '#1890ff', cursor: 'help' }}>(?)</span>
                </Tooltip>
              )}
            </div>
          } rules={rules}>
            <Checkbox.Group style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {field.checkboxConfig?.items.map((item) => (
                <Checkbox key={item.id} value={item.id}>
                  {item.label}
                </Checkbox>
              ))}
            </Checkbox.Group>
          </Form.Item>
        );
      case 'radio':
        return (
          <Form.Item key={field.id} name={field.id} label={
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>{field.name}</span>
              {field.tooltip && (
                <Tooltip title={field.tooltip}>
                  <span style={{ color: '#1890ff', cursor: 'help' }}>(?)</span>
                </Tooltip>
              )}
            </div>
          } rules={rules}>
            <Radio.Group options={field.options} />
          </Form.Item>
        );
      case 'range':
        return (
          <Form.Item key={field.id} name={field.id} label={
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>{field.name}</span>
              {field.tooltip && (
                <Tooltip title={field.tooltip}>
                  <span style={{ color: '#1890ff', cursor: 'help' }}>(?)</span>
                </Tooltip>
              )}
            </div>
          } rules={rules}>
            <Slider
              min={field.min}
              max={field.max}
              step={field.step}
            />
          </Form.Item>
        );
      case 'rating':
        return (
          <Form.Item key={field.id} name={field.id} label={
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>{field.name}</span>
              {field.tooltip && (
                <Tooltip title={field.tooltip}>
                  <span style={{ color: '#1890ff', cursor: 'help' }}>(?)</span>
                </Tooltip>
              )}
            </div>
          } rules={rules}>
            <Rate count={field.max || 5} />
          </Form.Item>
        );
      case 'image':
        return (
          <Form.Item key={field.id} label={
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>{field.name}</span>
              {field.tooltip && (
                <Tooltip title={field.tooltip}>
                  <span style={{ color: '#1890ff', cursor: 'help' }}>(?)</span>
                </Tooltip>
              )}
            </div>
          }>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <Space>
                <Button onClick={() => document.getElementById('equipmentFileInput')?.click()}>
                  Chọn ảnh từ local
                </Button>
                <Button onClick={handleOpenPicker}>
                  Chọn ảnh từ Google Drive
                </Button>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImagesChange}
                  id="equipmentFileInput"
                  style={{ display: 'none' }}
                />
              </Space>

              {/* Upload progress indicator */}
              {uploadProgress > 0 && uploadProgress < 100 && (
                <div style={{ marginTop: 8 }}>
                  <div>Đang tải ảnh lên: {uploadProgress}%</div>
                  <div style={{ width: '100%', backgroundColor: '#f0f0f0', borderRadius: 4 }}>
                    <div
                      style={{
                        width: `${uploadProgress}%`,
                        backgroundColor: '#1890ff',
                        height: 8,
                        borderRadius: 4,
                        transition: 'width 0.3s ease'
                      }}
                    />
                  </div>
                </div>
              )}

              {/* Local Files Preview */}
              {selectedFiles.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  <div>Ảnh từ local:</div>
                  <Space wrap>
                    {selectedFiles.map((file, index) => (
                      <Tag
                        key={index}
                        color="blue"
                        closable
                        onClose={() => handleRemoveLocalImage(index)}
                        style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}
                      >
                        <Space>
                          File local
                          <Image
                            src={getLocalPreviewUrl(file)}
                            alt={file.name}
                            style={{ width: 30, height: 30, objectFit: 'cover' }}
                          />
                        </Space>
                      </Tag>
                    ))}
                  </Space>
                </div>
              )}

              {/* Drive Images Preview */}
              {driveImages.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  <div>Ảnh từ Drive:</div>
                  <Space wrap>
                    {driveImages.map((image, index) => (
                      <Tag
                        key={index}
                        color="green"
                        closable
                        onClose={() => handleRemoveDriveImage(index)}
                        style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}
                      >
                        <Space>
                          Google Drive
                          <Image
                            src={`https://lh3.googleusercontent.com/d/${image.fileId}?authuser=server`}
                            alt={image.fileName}
                            style={{ width: 30, height: 30, objectFit: 'cover' }}
                          />
                        </Space>
                      </Tag>
                    ))}
                  </Space>
                </div>
              )}

              {/* Existing Images Preview */}
              {((selectedFilesPreview && selectedFilesPreview.length > 0) ||
                (driveImagesPreview && driveImagesPreview.length > 0)) && (
                  <div>
                    <div>Ảnh hiện tại:</div>

                    {selectedFilesPreview && selectedFilesPreview.length > 0 && (
                      <Space wrap>
                        {selectedFilesPreview.map((file, index) => (
                          <Tag
                            key={index}
                            color="orange"
                            closable
                            onClose={() => handleRemoveLocalPreviewImage(index)}
                            style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}
                          >
                            <Space>
                              File hiện tại
                              <Image
                                src={file}
                                alt={`file-${index}`}
                                style={{ width: 30, height: 30, objectFit: 'cover' }}
                              />
                            </Space>
                          </Tag>
                        ))}
                      </Space>
                    )}

                    {driveImagesPreview && driveImagesPreview.length > 0 && (
                      <Space wrap>
                        {driveImagesPreview.map((image, index) => (
                          <Tag
                            key={index}
                            color="purple"
                            closable
                            onClose={() => handleRemoveDrivePreviewImage(index)}
                            style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}
                          >
                            <Space>
                              Drive hiện tại
                              <Image
                                src={`https://lh3.googleusercontent.com/d/${image.fileId}?authuser=server`}
                                alt={image.fileName}
                                style={{ width: 30, height: 30, objectFit: 'cover' }}
                              />
                            </Space>
                          </Tag>
                        ))}
                      </Space>
                    )}
                  </div>
                )}
            </div>
          </Form.Item>
        );
      default:
        return (
          <Form.Item key={field.id} name={field.id} label={
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>{field.name}</span>
              {field.tooltip && (
                <Tooltip title={field.tooltip}>
                  <span style={{ color: '#1890ff', cursor: 'help' }}>(?)</span>
                </Tooltip>
              )}
            </div>
          } rules={rules}>
            <Input placeholder={field.placeholder} />
          </Form.Item>
        );
    }
  };

  const showEquipmentModal = (category: CoffeeEquipmentCategory) => {
    setCurrentCategory(category);
    setEditedFields(category.fields);
    setIsEquipmentModalVisible(true);
    equipmentForm.resetFields();
  };

  const showEditFieldsModal = (category: CoffeeEquipmentCategory) => {
    setEditingCategory(category);
    setEditedFields(category.fields);
    setIsEditFieldsModalVisible(true);
  };

  const showEquipmentListModal = async (category: CoffeeEquipmentCategory) => {
    try {
      setLoading(true);
      setCurrentCategory(category);
      const categoryEquipmentList = await getEquipmentByCategoryCached(category.id);
      setEquipment(categoryEquipmentList);
      setIsEquipmentListModalVisible(true);
    } catch (error) {
      console.error('Error loading equipment:', error);
      message.error('Không thể tải danh sách dụng cụ');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteEquipment = async (equipmentId: string) => {
    try {
      setLoading(true);
      
      // Hiển thị thông báo bắt đầu xóa
      const hideLoading = message.loading('Đang xóa dụng cụ và dữ liệu liên quan...', 0);
      
      await coffeeEquipmentService.deleteEquipment(equipmentId);
      
      // Ẩn thông báo loading
      hideLoading();
      
      // Cập nhật state
      setEquipment(prev => prev.filter(e => e.id !== equipmentId));
      if (currentCategory?.id) {
        bumpCategoryEquipmentCount(currentCategory.id, -1);
        const cache = equipmentByCategoryCacheRef.current.get(currentCategory.id);
        if (cache) {
          equipmentByCategoryCacheRef.current.set(currentCategory.id, {
            ...cache,
            equipment: cache.equipment.filter(e => e.id !== equipmentId),
            fetchedAt: Date.now()
          });
        }
      }

      message.success({
        content: 'Đã xóa dụng cụ và tất cả dữ liệu liên quan thành công!',
        duration: 3,
      });
    } catch (error) {
      console.error('Error deleting equipment:', error);
      message.error('Không thể xóa dụng cụ');
    } finally {
      setLoading(false);
    }
  };

  const handleAddNewOption = async (fieldId: string) => {
    try {
      if (!currentCategory || !newOptionText.trim()) return;

      setLoading(true);

      const currentField = currentCategory.fields.find(f => f.id === fieldId);
      if (!currentField || currentField.type !== 'select') return;

      const newOption = {
        label: newOptionText.trim(),
        value: newOptionText.trim().toLowerCase().replace(/\s+/g, '_')
      };

      const updatedFields = currentCategory.fields.map(field => {
        if (field.id === fieldId) {
          const currentOptions = Array.isArray(field.options) ? field.options : [];
          return {
            ...field,
            options: [...currentOptions, newOption]
          };
        }
        return field;
      });

      await coffeeEquipmentService.updateCategory(currentCategory.id, {
        fields: updatedFields
      });

      setCurrentCategory(prevCategory => ({
        ...prevCategory!,
        fields: updatedFields
      }));
      setCategories(prev => prev.map(cat => (
        cat.id === currentCategory.id ? { ...cat, fields: updatedFields } : cat
      )));

      setNewOptionText('');
      message.success('Lựa chọn mới đã được thêm thành công');
    } catch (error) {
      console.error('Error adding new option:', error);
      message.error('Không thể thêm lựa chọn mới');
    } finally {
      setLoading(false);
    }
  };

  // Tour steps
  const steps: TourProps['steps'] = [
    {
      title: 'Chào mừng đến với Quản lý dụng cụ cà phê!',
      description: 'Đây là nơi bạn có thể quản lý tất cả các loại dụng cụ cà phê và các dụng cụ cụ thể.',
      target: () => titleRef.current!,
    },
    {
      title: 'Thêm loại dụng cụ mới',
      description: 'Nhấp vào nút này để tạo loại dụng cụ mới với các trường thông tin tùy chỉnh.',
      target: () => addButtonRef.current!,
    },
    {
      title: 'Tìm kiếm dụng cụ',
      description: 'Sử dụng ô tìm kiếm này để tìm dụng cụ theo tên loại hoặc thông tin.',
      target: () => searchRef.current!,
    },
    {
      title: 'Danh sách loại dụng cụ',
      description: 'Bảng này hiển thị tất cả loại dụng cụ với số lượng trường và dụng cụ của từng loại.',
      target: () => tableRef.current!,
    },
    {
      title: 'Kết thúc hướng dẫn!',
      description: 'Bây giờ bạn đã sẵn sàng quản lý dụng cụ cà phê một cách hiệu quả!',
      target: () => titleRef.current!,
    },
  ];



  const categoryColumns: ColumnType<CoffeeEquipmentCategory>[] = [
    {
      title: 'Tên loại dụng cụ',
      dataIndex: 'name',
      key: 'name',
      render: (text: string) => (
        <Space>
          <AppstoreOutlined style={{ color: '#1890ff' }} />
          <span>{text}</span>
        </Space>
      ),
    },
    {
      title: 'Mô tả',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (text: string) => text || '-',
    },
    {
      title: 'Số trường thông tin',
      dataIndex: 'fields',
      key: 'fieldsCount',
      align: 'center',
      width: 150,
      render: (fields: CoffeeEquipmentField[]) => (
        <Tag color="blue">{fields.length}</Tag>
      ),
    },
    {
      title: 'Số dụng cụ',
      key: 'equipmentCount',
      align: 'center',
      width: 120,
      render: (record: CoffeeEquipmentCategory) => (
        <Tag color="green">{categoryEquipment[record.id] || 0}</Tag>
      ),
    },
    {
      title: 'Ngày tạo',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      render: (date: Date) => dayjs(date).format('DD/MM/YYYY HH:mm'),
    },
    {
      title: 'Cập nhật lần cuối',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 180,
      render: (date: Date) => dayjs(date).format('DD/MM/YYYY HH:mm'),
    },
    {
      title: 'Thao tác',
      key: 'action',
      fixed: 'right' as const,
      width: 200,
      render: (_: any, record: CoffeeEquipmentCategory) => (
        <div style={{ display: 'flex', gap: 8 }}>
          <Tooltip title="Thêm dụng cụ">
            <Button
              type="primary"
              icon={<PlusCircleOutlined />}
              onClick={() => showEquipmentModal(record)}
              className="admin-button"
            />
          </Tooltip>

          <Tooltip title="Chỉnh sửa trường">
            <Button
              icon={<EditOutlined />}
              onClick={() => showEditFieldsModal(record)}
              className="admin-button"
            />
          </Tooltip>

          <Tooltip title="Danh sách dụng cụ">
            <Button
              icon={<AppstoreOutlined />}
              onClick={() => showEquipmentListModal(record)}
              className="admin-button"
            />
          </Tooltip>

          <Tooltip title="Xóa loại dụng cụ">
            <Popconfirm
              title="Xóa loại dụng cụ"
              description="Bạn có chắc chắn muốn xóa loại dụng cụ này? Tất cả dụng cụ thuộc loại này cùng với dữ liệu liên quan (lịch sử xem, yêu thích, đơn hàng) sẽ bị xóa."
              onConfirm={() => handleDeleteCategory(record.id)}
              okText="Xóa"
              cancelText="Hủy"
              okButtonProps={{ danger: true }}
            >
              <Button
                danger
                icon={<DeleteOutlined />}
                className="admin-button"
              />
            </Popconfirm>
          </Tooltip>
        </div>
      ),
    },
  ];

  const equipmentListColumns = [
    {
      title: 'Hình ảnh',
      dataIndex: 'images',
      key: 'images',
      width: 120,
      render: (images: string[], record: CoffeeEquipment) => (
        <Image.PreviewGroup>
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 8,
            maxWidth: 120,
          }}>
            {(record.driveImages && record.driveImages.length > 0) && record.driveImages.map((image, index) => (
              <div key={index} style={{ marginBottom: 8 }}>
                <Image
                  src={`https://lh3.googleusercontent.com/d/${image.fileId}?authuser=server`}
                  alt={image.fileName}
                  style={{ width: 50, height: 50, objectFit: 'cover' }}
                />
              </div>
            ))}

            {images && images.map((url, index) => (
              <div key={index} style={{ marginBottom: 8 }}>
                <Image
                  src={url}
                  alt={`equipment-${index}`}
                  width={50}
                  height={50}
                  style={{ objectFit: 'cover' }}
                />
              </div>
            ))}
          </div>
        </Image.PreviewGroup>
      )
    },
    {
      title: 'Thông tin',
      key: 'values',
      render: (record: CoffeeEquipment) => (
        <div style={{ display: 'grid', gap: '8px' }}>
          {record.values.slice(0, 3).map((field) => {
            let displayValue;
            const fieldConfig = currentCategory?.fields.find(f => f.id === field.id);

            switch (field.type) {
              case 'date':
                displayValue = field.value?.seconds ?
                  dayjs(field.value.toDate()).format('DD/MM/YYYY') :
                  dayjs(field.value).format('DD/MM/YYYY');
                break;
              case 'number':
                displayValue = (
                  <span>
                    {field.value?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')}
                    {fieldConfig?.suffix && ` ${fieldConfig.suffix}`}
                  </span>
                );
                break;
              case 'number_range':
                displayValue = (
                  <span>
                    {field.value?.from?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')}
                    {fieldConfig?.suffix && ` ${fieldConfig.suffix}`}
                    {' - '}
                    {field.value?.to?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')}
                    {fieldConfig?.suffix && ` ${fieldConfig.suffix}`}
                  </span>
                );
                break;
              case 'select':
                if (Array.isArray(field.value)) {
                  displayValue = (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                      {field.value.slice(0, 2).map((v: string, i: number) => (
                        <span
                          key={i}
                          style={{
                            padding: '2px 6px',
                            backgroundColor: '#e6f4ff',
                            color: '#1677ff',
                            borderRadius: '12px',
                            fontSize: '12px'
                          }}
                        >
                          {fieldConfig?.options?.find(opt => opt.value === v)?.label || v}
                        </span>
                      ))}
                      {field.value.length > 2 && <span>...</span>}
                    </div>
                  );
                } else {
                  displayValue = fieldConfig?.options?.find(opt => opt.value === field.value)?.label || field.value;
                }
                break;
              case 'checkbox':
                displayValue = (
                  <span style={{
                    color: field.value ? '#52c41a' : '#ff4d4f',
                    fontWeight: 'bold'
                  }}>
                    {field.value ? '✓' : '✗'}
                  </span>
                );
                break;
              default:
                displayValue = typeof field.value === 'string' && field.value.length > 30 
                  ? field.value.substring(0, 30) + '...' 
                  : field.value;
            }

            return (
              <div
                key={field.id}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '8px',
                  padding: '4px 8px',
                  backgroundColor: '#f5f5f5',
                  borderRadius: '4px'
                }}
              >
                <span style={{ fontWeight: 500, minWidth: '80px', fontSize: '12px' }}>{field.name}:</span>
                <div style={{ flex: 1, fontSize: '12px' }}>
                  {displayValue || <span style={{ color: '#bfbfbf', fontStyle: 'italic' }}>-</span>}
                </div>
              </div>
            );
          })}
          {record.values.length > 3 && (
            <div style={{ fontSize: '12px', color: '#999', textAlign: 'center' }}>
              ...và {record.values.length - 3} trường khác
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Ngày tạo',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      render: (date: Date) => dayjs(date).format('DD/MM/YYYY HH:mm'),
    },
    {
      title: 'Thao tác',
      key: 'action',
      width: 150,
      render: (record: CoffeeEquipment) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />
          <Popconfirm
            title="Xóa dụng cụ"
            description="Bạn có chắc chắn muốn xóa dụng cụ này? Tất cả dữ liệu liên quan (lịch sử xem, yêu thích, đơn hàng) cũng sẽ bị xóa."
            onConfirm={() => handleDeleteEquipment(record.id)}
            okText="Xóa"
            cancelText="Hủy"
            okButtonProps={{ danger: true }}
          >
            <Button
              danger
              icon={<DeleteOutlined />}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const modalConfig = {
    title: isEditing
      ? `Chỉnh sửa ${currentCategory?.name || 'Dụng cụ'}`
      : `Thêm ${currentCategory?.name || 'Dụng cụ'} Mới`,
    okText: isEditing ? 'Cập nhật' : 'Thêm',
  };

  return (
    <ConfigProvider locale={viVN}>
      <AdminLayout>
        <div className="admin-page-header">
          <div className="admin-page-title">
            <h1 ref={titleRef}>
              Quản lý dụng cụ cà phê
            </h1>
            <Tag color="blue">{categories.length} loại dụng cụ</Tag>
            <Button
              type="primary"
              icon={<QuestionCircleOutlined />}
              onClick={() => setOpen(true)}
              style={{ marginLeft: 8 }}
            >
              Hướng dẫn
            </Button>
          </div>

          <div className="admin-page-actions">
            <div className="admin-search-filters">
              <div ref={searchRef}>
                <Search
                  placeholder="Tìm kiếm theo tên loại dụng cụ..."
                  allowClear
                  style={{ width: 300 }}
                  onChange={(e) => setSearchText(e.target.value)}
                />
              </div>
            </div>

            <Space>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  setIsNewCategoryModalVisible(true);
                  categoryForm.resetFields();
                }}
                className="admin-button"
                ref={addButtonRef}
              >
                Thêm loại dụng cụ
              </Button>
            </Space>
          </div>
        </div>

        <div className="admin-table-container" ref={tableRef}>
          <Table
            className="admin-table"
            columns={categoryColumns}
            dataSource={categories}
            rowKey="id"
            loading={loading}
            scroll={{ x: 1400 }}
            pagination={{
              responsive: true,
              defaultPageSize: 10,
              position: ['bottomRight'],
              total: categories.length,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} của ${total} loại dụng cụ`,
            }}
          />
        </div>

        {/* Modal tạo loại dụng cụ mới */}
        <Modal
          title={
            <Space>
              <PlusCircleOutlined style={{ color: '#52c41a' }} />
              <span>Tạo loại dụng cụ mới</span>
            </Space>
          }
          open={isNewCategoryModalVisible}
          onOk={handleCreateCategory}
          onCancel={() => setIsNewCategoryModalVisible(false)}
          okText="Tạo"
          cancelText="Hủy"
          width={600}
          confirmLoading={loading}
          className="admin-modal"
        >
          <div className="admin-form-container">
            <Form
              form={categoryForm}
              layout="vertical"
            >
              <Form.Item
                name="name"
                label="Tên loại dụng cụ"
                rules={[{ required: true, message: 'Vui lòng nhập tên loại dụng cụ' }]}
              >
                <Input placeholder="Ví dụ: Máy pha cà phê, Máy xay cà phê, Bình pha..." />
              </Form.Item>

              <Form.Item
                name="description"
                label="Mô tả"
              >
                <TextArea 
                  placeholder="Mô tả về loại dụng cụ này..."
                  rows={3}
                />
              </Form.Item>
            </Form>
          </div>
        </Modal>

        {/* Modal thêm/sửa dụng cụ */}
        <Modal
          title={
            <Space>
              {isEditing ? (
                <EditOutlined style={{ color: '#1890ff' }} />
              ) : (
                <PlusCircleOutlined style={{ color: '#52c41a' }} />
              )}
              <span>{modalConfig.title}</span>
            </Space>
          }
          open={isEquipmentModalVisible}
          onOk={handleSubmit}
          onCancel={() => {
            setIsEquipmentModalVisible(false);
            setIsEditing(false);
            setEditingEquipment(null);
            setSelectedFiles([]);
            setSelectedFilesPreview([]);
            setDriveImages([]);
            setDriveImagesPreview([]);
            equipmentForm.resetFields();
          }}
          okText={modalConfig.okText}
          cancelText="Hủy"
          width={800}
          confirmLoading={submitLoading}
          className="admin-modal"
        >
          <div className="admin-form-container">
            <Form form={equipmentForm} layout="vertical">
              {currentCategory?.fields.map(renderField)}
            </Form>
          </div>
        </Modal>

        {/* Modal chỉnh sửa trường thông tin */}
        <Modal
          title={
            <Space>
              <EditOutlined style={{ color: '#1890ff' }} />
              <span>Chỉnh sửa trường thông tin - {editingCategory?.name}</span>
            </Space>
          }
          open={isEditFieldsModalVisible}
          onOk={handleEditFields}
          onCancel={() => {
            setIsEditFieldsModalVisible(false);
            setEditingCategory(null);
            setEditedFields([]);
          }}
          okText="Lưu"
          cancelText="Hủy"
          width={800}
          confirmLoading={loading}
          className="admin-modal"
        >
          <div className="admin-form-container">
            <CoffeeEquipmentFieldsManager
              fields={editedFields}
              onChange={setEditedFields}
            />
          </div>
        </Modal>

        {/* Modal danh sách dụng cụ */}
        <Modal
          title={
            <Space>
              <AppstoreOutlined style={{ color: '#1890ff' }} />
              <span>Danh sách {currentCategory?.name || 'Dụng cụ'}</span>
            </Space>
          }
          open={isEquipmentListModalVisible}
          onCancel={() => {
            setIsEquipmentListModalVisible(false);
            setCurrentCategory(null);
            setEquipment([]);
            setSearchText('');
          }}
          footer={[
            <Button
              key="add"
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setIsEquipmentListModalVisible(false);
                setIsEquipmentModalVisible(true);
                equipmentForm.resetFields();
              }}
            >
              Thêm {currentCategory?.name || 'Dụng cụ'}
            </Button>,
            <Button
              key="close"
              onClick={() => {
                setIsEquipmentListModalVisible(false);
                setCurrentCategory(null);
                setEquipment([]);
                setSearchText('');
              }}
            >
              Đóng
            </Button>
          ]}
          width={1200}
          className="admin-modal"
        >
          <div className="admin-form-container">
            <div style={{ marginBottom: 16 }}>
              <Search
                placeholder="Tìm kiếm dụng cụ..."
                allowClear
                style={{ width: 300 }}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </div>
            
            <div className="admin-table-container">
              <Table
                className="admin-table"
                columns={equipmentListColumns}
                dataSource={filteredEquipment}
                rowKey="id"
                loading={loading}
                scroll={{ x: 1000 }}
                pagination={{
                  pageSize: 10,
                  showSizeChanger: true,
                  showTotal: (total) => `Tổng số: ${total} dụng cụ`,
                }}
              />
            </div>
          </div>
        </Modal>

        {/* Tour Guide */}
        <Tour open={open} onClose={() => setOpen(false)} steps={steps} />
      </AdminLayout>
    </ConfigProvider>
  );
};

export default CoffeeEquipmentManagement;
